import { atomWithSessionStorage } from '@/utils/browserStorage';
import { appStore } from '@/utils/jotaiStore';
import { safeRelativeRedirect, toNullable } from '@/utils/strings';
import { useAtomValue, useSetAtom } from 'jotai';
import moment from 'moment-timezone';
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router';
import { z } from 'zod';

// Returning a user to a page they left long ago is more disorienting than helpful, and
//  the stored page outlives the session it belongs to, so it expires.
const MAX_RESTORE_AGE_MINUTES = 30;

// Login-entry paths are transient stops on the way into the app. Storing one would mean
//  returning a user to a sign-in page instead of a real destination.
const LOGIN_ENTRY_PATHS = ['/sign-in', '/sso', '/sso-callback'];

const lastAuthedPageSchema = z
    .object({
        userUuid: z.string(),
        path: z.string(),
        savedAt: z.string(),
    })
    .nullable();

type LastAuthedPage = z.infer<typeof lastAuthedPageSchema>;

// The page an authenticated user was last on, scoped to the tab that stored it and to
//  the user it belonged to. Session storage keeps the page out of the URL, so a tab
//  whose session ended looks like a clean sign-in page, and gives every tab its own
//  record, so each one returns to where it was rather than to a sibling's page.
// The key is registered in PERSISTENT_SESSION_STORAGE_KEYS, as clearing local data on
//  logout would otherwise wipe the page we want to return to.
const lastAuthedPageAtom = atomWithSessionStorage('lastAuthedPage', null, lastAuthedPageSchema);

// Returns the stored page when it belongs to the given user and is recent enough.
//  A user without a UUID (a session authenticated before the backend started sending
//  one) has no identity to scope a page to, so nothing is returned.
const pageForUser = (
    lastAuthedPage: LastAuthedPage,
    userUuid: string | undefined,
): string | null => {
    if (!userUuid || !lastAuthedPage || lastAuthedPage.userUuid !== userUuid) {
        return null;
    }

    if (moment().diff(moment(lastAuthedPage.savedAt), 'minutes') >= MAX_RESTORE_AGE_MINUTES) {
        return null;
    }

    return lastAuthedPage.path;
};

// Keeps this tab's stored page in step with where the user is, so that whenever their
//  session ends, the page to return to is already recorded. This has to happen while
//  the user is still authenticated: a session ending in a sibling tab reaches this one
//  as a `storage` event that flips the auth state, and runs no logout code here.
export const useRecordLastAuthedPage = (userUuid: string | undefined) => {
    const location = useLocation();
    const setLastAuthedPage = useSetAtom(lastAuthedPageAtom);

    const path = location.pathname + location.search;
    const isLoginEntryPath = LOGIN_ENTRY_PATHS.includes(location.pathname);

    useEffect(() => {
        if (!userUuid || isLoginEntryPath) {
            return;
        }

        setLastAuthedPage({ userUuid, path, savedAt: moment().toISOString() });
    }, [userUuid, path, isLoginEntryPath, setLastAuthedPage]);
};

export const useClearLastAuthedPage = () => {
    const setLastAuthedPage = useSetAtom(lastAuthedPageAtom);

    return useCallback(() => setLastAuthedPage(null), [setLastAuthedPage]);
};

// Resolves where to send a user once they are authenticated: an explicit destination
//  carried through the sign-in flow wins, then this tab's stored page, then the
//  landing page.
export const usePostAuthRedirect = (destination: string, userUuid: string | undefined): string => {
    const lastAuthedPage = useAtomValue(lastAuthedPageAtom);

    return safeRelativeRedirect(
        toNullable(destination) ?? pageForUser(lastAuthedPage, userUuid) ?? '/',
    );
};

// The same resolution for code that authenticates a user and navigates onwards itself,
//  where the user's UUID is known before the auth state has propagated to hooks.
export const resolvePostAuthRedirect = (
    destination: string,
    userUuid: string | undefined,
): string => {
    return safeRelativeRedirect(
        toNullable(destination) ?? pageForUser(appStore.get(lastAuthedPageAtom), userUuid) ?? '/',
    );
};
